import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {assert} from 'Asserts';
import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {onCreateExperiments} from 'studies/onCreateExperiments';
import useTimingEstimate, {TimingEstimateResult} from 'studies/progress/useTimingEstimate';
import {
  getExperimentUnknownErrorAction,
  onOpenAfterExperimentCreate,
  onSuccessAfterExperimentCreate,
} from 'studies/sidepanel/ExperimentCreationTrayTask';
import {EXPERIMENT_NAME_MAX_LENGTH} from 'studies/StudyConstants';
import {
  BulkCreateExperimentsParam,
  CreateExperimentsResponse,
  NamedProcedureInformation,
  ProcedureInformation,
} from 'studies/StudyDAO';
import {ExperimentDesignEditor} from 'studies/typed/process-development/ExperimentDesignEditor';
import ExperimentNameField from 'studies/typed/process-development/ExperimentNameField';
import {
  ExperimentDesignState,
  useExperimentDesignState,
} from 'studies/typed/process-development/useProcessDevelopmentStudyState';
import {LabeledProcedureDropdown} from 'studies/typed/shared/LabeledProcedureDropdown';
import {
  getProcedureRunConditionTextForProcedureType,
  getProcedureTypeForStudyType,
} from 'studies/TypedStudyUtils';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import Modal, {ModalButton, SharedModalProps} from 'ui-library/modals/Modal';
import runModal from 'ui-library/runModal';
import StepNav, {StepNavVariant} from 'ui-library/StepNav';
import {assertNever} from 'util/Types';

import {shouldAllowSubmit} from '../typed/utils/ui/StudyModalUtils';
import ConfirmCancelModal from './ConfirmCancelModal';
import {NoMethodWarningBanner, NoProcedureWarningBanner} from './ModalWarningBanners';

export interface AddExperimentModalProps {
  onRequestClose: () => void;
  onRequestSubmit?: () => void;
  studyType: StudyType;
  studyId: string;
  initialProcedureId?: string;
}

enum AddExperimentStepKey {
  Metadata = 'Metadata',
  Experiment = 'Experiment',
}

/*
 * True if provided metadata is complete, valid, and should allow proceeding to design the experiment,
 * false otherwise.
 */
function validateMetadata(state: ExperimentDesignState): boolean {
  const experimentName = state.experimentName ?? '';
  if (experimentName.length > EXPERIMENT_NAME_MAX_LENGTH || experimentName.length === 0) {
    return false;
  }
  return state.procedureId != null && state.experimentName != null;
}

/* True if experiment is valid for creation, false otherwise. */
function validateExperiment(state: ExperimentDesignState): boolean {
  return !state.methodExecutionLimitState.isOverLimit;
}

/* Type guard to check if procedureInformation is non-null and has a name. */
function isNamedExperiment(
  procedureInformation: ProcedureInformation | null
): procedureInformation is Required<NamedProcedureInformation> {
  return procedureInformation != null && procedureInformation.name != null;
}

function getButtonsByStep(
  currentStep: AddExperimentStepKey,
  updateCurrentStep: (step: AddExperimentStepKey) => void,
  submissionDisabled: boolean,
  isMetadataValid: boolean
): Array<ModalButton> {
  switch (currentStep) {
    case AddExperimentStepKey.Metadata:
      return [
        {
          cancel: true,
          disabled: false,
          text: 'Cancel',
        },
        {
          disabled: !isMetadataValid,
          text: 'Next',
          style: ButtonStyle.SECONDARY,
          onClick: () => {
            updateCurrentStep(AddExperimentStepKey.Experiment);
          },
          dataTestAttrs: {component: 'AddExperimentModal', element: 'nextButton'},
        },
      ];
    case AddExperimentStepKey.Experiment:
      return [
        {
          cancel: true,
          disabled: false,
          text: 'Cancel',
        },
        {
          disabled: false,
          submit: false,
          text: 'Back',
          style: ButtonStyle.TERTIARY_GRAY,
          onClick: () => {
            updateCurrentStep(AddExperimentStepKey.Metadata);
          },
        },
        {
          disabled: submissionDisabled,
          submit: true,
          text: 'Add experiment',
          style: ButtonStyle.SECONDARY,
          dataTestAttrs: {component: 'AddExperimentModal', element: 'submitButton'},
        },
      ];
    default:
      assert(false, `Unexpected key: ${assertNever(currentStep)}`);
  }
}

function modalOptions(stepKey: AddExperimentStepKey, studyType: StudyType): SharedModalProps {
  if (stepKey === AddExperimentStepKey.Experiment) {
    return {
      extraLarge: true,
      bodyPadded: false,
      fullHeight: true,
      footer: (
        <InlineBanner
          bannerType={InlineBannerType.WARNING}
          text={`Upon creation, tasks will be generated and you won't be able to edit ${getProcedureRunConditionTextForProcedureType(
            getProcedureTypeForStudyType(studyType),
            {plural: true}
          )}.`}
        />
      ),
    };
  }
  return {};
}

function renderStep(
  stepKey: AddExperimentStepKey,
  props: AddExperimentModalProps,
  state: ExperimentDesignState,
  estimate: TimingEstimateResult
): JSX.Element {
  /*
   * Note: the experiment design step for this function assumes a procedure-based experiment design. If we
   * have a study type where adding an experiment entails something different from designing a procedure-based
   * experiment, we may want to hoist the rendering function for the experiment design step into the typed
   * study adapter to allow for this differentiation.
   */
  const procedureType = getProcedureTypeForStudyType(props.studyType);
  switch (stepKey) {
    case AddExperimentStepKey.Metadata:
      return (
        <>
          <ExperimentNameField
            experimentName={state.experimentName}
            setExperimentName={state.setExperimentName}
            contextLocked={state.context?.locked || false}
          />
          <LabeledProcedureDropdown
            procedureId={state.procedureId}
            setProcedureId={state.setProcedureId}
            onRequestCloseModal={props.onRequestClose}
            procedureType={procedureType}
            requiredField={true}
            dropdownDisabled={false}
            // We need the latest possible procedure version id because that gets stamped into the new run
            procedureFetchPolicy="network-only"
          />
        </>
      );
    case AddExperimentStepKey.Experiment:
      if (state.procedureId == null) {
        return <NoProcedureWarningBanner procedureType={procedureType} />;
      }
      if (state.procedure == null || state.context == null || state.conditions == null) {
        return <HorizontalThrobber />;
      }
      if (state.selectedMethodId == null) {
        return <NoMethodWarningBanner procedureType={procedureType} />;
      }

      return (
        <ExperimentDesignEditor
          conditions={state.conditions}
          setConditions={state.setConditions}
          experimentalInputs={state.experimentalInputs}
          setExperimentalInputs={state.setExperimentalInputs}
          context={state.context}
          procedureFlowchart={state.procedure.procedureFlowchart}
          procedureMethods={state.procedure.methods}
          selectedMethodId={state.selectedMethodId}
          setSelectedMethodId={state.setSelectedMethodId}
          methodExecutionLimitState={state.methodExecutionLimitState}
          estimate={estimate}
          procedureType={procedureType}
        />
      );
    default:
      assert(false, `Unexpected key: ${assertNever(stepKey)}`);
  }
}

export default function AddExperimentModal(props: AddExperimentModalProps): JSX.Element {
  const state = useExperimentDesignState({
    initialFolderId: null,
    initialProcedureId: props.initialProcedureId ?? null,
  });
  const [isBackground, setIsBackground] = useState(false);
  const steps: Array<{key: AddExperimentStepKey; title: string}> = [
    {
      key: AddExperimentStepKey.Metadata,
      title: 'Specify metadata',
    },
    {
      key: AddExperimentStepKey.Experiment,
      title: 'Design experiment',
    },
  ];
  const [currentStepKey, setCurrentStepKey] = useState(steps[0]?.key);
  const {experimentParam} = state;
  const isValidExperiment = validateExperiment(state);
  const estimateResult = useTimingEstimate<BulkCreateExperimentsParam>(
    '/1/api/studies/experiments:estimate',
    isNamedExperiment(experimentParam) && isValidExperiment ? {creates: [experimentParam]} : null
  );
  const handleSubmit =
    isNamedExperiment(experimentParam) && shouldAllowSubmit(steps, currentStepKey) && isValidExperiment
      ? async (): Promise<void> => {
          await onCreateExperiments(props.studyId, props.studyType, [experimentParam], {
            onSuccess: async (response: CreateExperimentsResponse) => {
              await onSuccessAfterExperimentCreate(props.studyId, response, props.studyType);
              props.onRequestClose();
            },
            onOpen: (response: CreateExperimentsResponse) => {
              onOpenAfterExperimentCreate(props.studyId);
            },
            onBackground: (isBackground: boolean) => {
              setIsBackground(isBackground);
            },
            onUnknownError: getExperimentUnknownErrorAction(props.studyId, props.studyType),
          });
        }
      : null;

  if (isBackground) {
    return <></>;
  }

  const handleClose = async (): Promise<void> => {
    const confirmCancel = await runModal(ConfirmCancelModal, {
      userFacingContextName: 'experiment creation',
    });
    if (confirmCancel != null) {
      props.onRequestClose();
    }
  };

  return (
    <Modal
      title={
        <div className={css(styles.titleWrapper)}>
          <span>Add experiment</span>
          <StepNav<AddExperimentStepKey>
            steps={steps}
            statusByStep={{Metadata: null, Experiment: null}}
            currentStepKey={currentStepKey}
            onUpdateCurrentStep={setCurrentStepKey}
            variant={StepNavVariant.FLAT}
          />
        </div>
      }
      onRequestClose={handleClose}
      onRequestSubmit={handleSubmit}
      buttons={getButtonsByStep(currentStepKey, setCurrentStepKey, !handleSubmit, validateMetadata(state))}
      {...modalOptions(currentStepKey, props.studyType)}
      closeableOnEscapeKey={false}
      closeableOnBackgroundClick={false}
    >
      {renderStep(currentStepKey, props, state, estimateResult)}
    </Modal>
  );
}

const styles = StyleSheet.create({
  titleWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: '50px',
    rowGap: 10,
  },
});
